import React, {useState} from 'react';
import styled from 'styled-components';

import {H2, Body3} from '@bootcamp/web/src/components/Typography/next';
import {RoundButton} from '@bootcamp/web/src/components/UI';
import {useModalContext} from '@bootcamp/web/src/contexts/Modal';
import {useUserDataContext} from '@bootcamp/web/src/contexts/UserData';
import CreateModal from './Modals/Create';

import {Element as FAQElement} from '@bootcamp/web/src/bootcamps/components/WebflowFAQs';
import {placeholderMap, testTitleMap, studyScheduleLinkMap} from '@bootcamp/web/src/bootcamps/pages/StudySchedule/components/shared';
const Container = styled.div`
`;
const Wrapper = styled.div`
  background: white;
  border-radius: 8px;
`;
const Hero = styled.div`
  padding: 32px 32px 12px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: auto;

  ${H2} {
    margin-bottom: 8px;
  }

  ${Body3} {
    margin-bottom: 16px;
    max-width: 725px;
  }

  ${({theme}) => theme.mediaQueries.tablet} {
    padding: 24px 24px 12px;
    text-align: left;
    align-items: flex-start;

    ${H2} {
      margin-bottom: 4px;
    }
  }
`;
const CalendarImageWrapper = styled.div`
  position: relative;
  padding-bottom: 32%;
  overflow: hidden;

  ${({theme}) => theme.mediaQueries.tablet} {
    padding-bottom: 136%;
  }
`;
const CalendarImage = styled.img`
  position: absolute;
  width: 100%;

  ${({theme}) => theme.mediaQueries.tablet} {
    display: none;
  }
`;

const CalendarImageMobile = styled(CalendarImage)`
  display: none;
  width: calc(100% + 24px);
  right: -16px;

  ${({theme}) => theme.mediaQueries.tablet} {
    display: block;
  }
`;
const FAQHeader = styled(H2)`
  color: white;
  margin-top: 24px;
  margin-bottom: 16px;
`;

const Create = ({onCreate, totalHours}) => {
  const {modalDispatch} = useModalContext();
  const [state, setState] = useState();
  const {searchUserInteractions, bootcamp} = useUserDataContext();
  const examDate = searchUserInteractions('testDate', null);
  const faqItems = [
    {
      title: "Why should I use the study schedule generator?",
      text: `
      Students who use a study schedule score higher than those that don’t. But PDF study schedules are rigid and annoying to use. So we took our proven ${testTitleMap[bootcamp]} study schedule PDF and turned it into an interactive customizable calendar!
      <ul>
        <li>Select your start and exam date, and the generator will create a personalized study schedule.</li>
        <li>Easily customize your schedule by adding days off, moving assignments, or changing your exam date.</li>
        <li>If you fall behind, click Rebalance and the generator will evenly distribute your overdue assignments to your exam date.</li>
      </ul>
      This tool will help you stay on track to ace your exam. Create a personalized schedule today and get started!
    `
    },
    {
      title: "I already started the PDF schedule, how can I switch to the study schedule generator?",
      text: `
      You can still use the study schedule generator even if you're partway through the PDF schedule!
        <ol>
          <li>Generate a schedule with today as your start date. </li>
          <li>Mark all the assignments you've already done as complete. </li>
          <li>Then click Rebalance, and the study schedule will evenly spread your remaining assignments up to your exam date.</li>
        </ol>
    `
    },
    {
      title: "Is this the same as the PDF study schedule?",
      text: `Yes. There is no difference in content, they both have the same assignments and the same order. We recommend using the study schedule generator so you can customize the schedule to your study dates and easily update it. You can view the original <a href='${studyScheduleLinkMap[bootcamp] || 'https://bootcamp.com/nclex/study-schedule'}' rel='noreferrer' target='_blank'>Study Schedule PDF here.</a>`
    },
    {
      title: "What if I fall behind or need to change something?",
      text: "No worries, you can click Rebalance and the generator will evenly distribute your overdue assignments up to your exam date. You can move assignments, take days off, or change your exam date. You’re in full control."
    },
  ];

  const summonModal = () => {
    if (!totalHours) {
      window.alert('Sorry, there is a problem with our study schedule generator. Please reach out to our support team via the Intercom button, or try again later.');
      return;
    }
    modalDispatch({
      type: 'open',
      component: CreateModal,
      enableClickClose: true,
      componentProps: {
        onCreate,
        totalHours,
        startingExamDate: examDate,
      },
      modalContainerStyle: () => `
        overflow: hidden;
      `,
    })
  };
  return (
    <Container>
      <Wrapper>
        <Hero>
          <H2>Everything you need to stay on track.</H2>
          <Body3>We’ve taken our well-loved PDF schedules and turned them into personalized schedules that are made just for you. Whether you're months or weeks away, we simplify planning and help you stay on track.</Body3>
          <RoundButton onClick={summonModal} small>Create Study Schedule</RoundButton>
        </Hero>
          <CalendarImageWrapper>
            <CalendarImage src={placeholderMap[bootcamp]}/>
            <CalendarImageMobile src={placeholderMap['mobile']}/>
          </CalendarImageWrapper>
      </Wrapper>
      <FAQHeader>FAQs</FAQHeader>
      {faqItems.map(({title, text}) => <FAQElement title={title} text={text} />)}
    </Container>
  );
};

Create.defaultProps = {};
Create.propTypes = {};

export default Create;